import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import API_BASE from '../utils/apiBase';
import { getLocalApplications } from '../utils/jobStorage';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08, delayChildren: 0.2 } }
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const getCvUrl = (fileUrl) => {
  if (!fileUrl) return '#';
  if (fileUrl.startsWith('data:') || fileUrl.startsWith('blob:') || fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }
  const cleanPath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
  const BASE_URL = (process.env.REACT_APP_API_BASE || "https://palegreen-snake-884644.hostingersite.com/backend").replace(/\/api$/, "");
  return `${BASE_URL}${cleanPath}`;
};

const getDownloadFileName = (app) => {
  // 1. Check if explicit file_name is present
  if (app.file_name && app.file_name.trim() !== '') {
    return app.file_name;
  }
  // 2. Extract original filename from file_url path if present
  if (app.file_url && !app.file_url.startsWith('data:')) {
    const rawPath = app.file_url.split('?')[0];
    const segments = rawPath.split('/');
    const lastSegment = segments[segments.length - 1];
    if (lastSegment && lastSegment.includes('.')) {
      return lastSegment;
    }
  }
  // 3. Inspect MIME type if Data URL
  let extension = 'pdf';
  if (app.file_url && app.file_url.startsWith('data:')) {
    const mimeMatch = app.file_url.match(/^data:([^;]+);/);
    if (mimeMatch) {
      const mime = mimeMatch[1].toLowerCase();
      if (mime.includes('word') || mime.includes('document')) extension = 'docx';
      else if (mime.includes('pdf')) extension = 'pdf';
      else if (mime.includes('png')) extension = 'png';
      else if (mime.includes('jpeg') || mime.includes('jpg')) extension = 'jpg';
    }
  }
  const candidateName = app.name ? app.name.trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '_') : 'Candidate';
  return `${candidateName}_CV.${extension}`;
};

const AdminApplicationsList = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/job_applications.php`)
      .then(async (res) => {
        const bodyText = await res.text();
        let data = null;
        try {
          data = JSON.parse(bodyText);
        } catch {
          data = null;
        }

        if (data && data.success && Array.isArray(data.applications) && data.applications.length > 0) {
          setApplications(data.applications);
        } else {
          setApplications(getLocalApplications());
        }
        setError("");
        setLoading(false);
      })
      .catch(() => {
        setApplications(getLocalApplications());
        setError("");
        setLoading(false);
      });
  }, []);

  const handleDownload = async (app, e) => {
    e.preventDefault();
    const rawUrl = getCvUrl(app.file_url);
    const fileName = getDownloadFileName(app);

    if (!rawUrl || rawUrl === '#') {
      alert("CV file is not available.");
      return;
    }

    // Direct download for Data / Blob URLs
    if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) {
      const link = document.createElement('a');
      link.href = rawUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Safe Blob fetch for remote server files to prevent cross-origin 404 errors
    try {
      const encodedUrl = encodeURI(rawUrl);
      const res = await fetch(encodedUrl);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch {
      alert(`Unable to download: The file "${fileName}" was not found on the server.`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4" style={{ marginTop: "90px" }}>
      <div className="max-w-4xl mx-auto bg-black rounded-xl shadow-xl p-6 border border-white/10">
        <h2 className="text-2xl font-bold mb-6 text-white">Job Applications</h2>
        {loading ? (
          <div className="text-center text-blue-400">Loading...</div>
        ) : error ? (
          <div className="text-center text-red-400">{error}</div>
        ) : applications.length === 0 ? (
          <div className="text-center text-gray-400">No applications found.</div>
        ) : (
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {applications.map(app => {
              const fileName = getDownloadFileName(app);
              const cvUrl = getCvUrl(app.file_url);
              return (
                <motion.div
                  key={app.id}
                  className="bg-slate-900 border border-white/10 rounded-xl p-5 flex flex-col md:flex-row md:items-center md:justify-between shadow-md hover:border-blue-500/50 transition-colors"
                  variants={itemVariants}
                >
                  <div>
                    <div className="font-semibold text-lg text-white">{app.name}</div>
                    <div className="text-gray-300 text-sm">{app.email} | {app.birthdate}</div>
                    <div className="text-gray-300 text-sm">Position: <span className="font-medium text-sky-400">{app.position}</span></div>
                    <div className="text-gray-300 text-sm">Work Ex: {app.workex}</div>
                    <div className="text-gray-400 text-sm mt-1">Message: {app.message}</div>
                    <div className="text-gray-500 text-xs mt-2">Applied: {app.applied_at}</div>
                  </div>
                  <div className="mt-4 md:mt-0 md:ml-4 flex flex-col items-end">
                    {app.file_url ? (
                      <a
                        href={cvUrl}
                        download={fileName}
                        onClick={(e) => handleDownload(app, e)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors shadow flex items-center gap-2"
                        title={`Download ${fileName}`}
                      >
                        <span>📥</span> Download CV
                      </a>
                    ) : (
                      <span className="text-xs text-gray-500 italic">No CV uploaded</span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminApplicationsList;
